/**
 * LevelUpCelebration - Full-screen celebration modal for level ups
 *
 * Features:
 * - Dramatic entrance animation
 * - New level number and title display
 * - Confetti animation
 * - Share button for social
 * - Unlocked rewards display
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { cn } from '@/lib/utils';
import LevelingService from '@bricksOS/valets/logic/LevelingService';
import {
  Trophy,
  Star,
  Sparkles,
  Share2,
  X,
  ChevronRight,
  Zap,
  Crown,
  Rocket,
  Gift,
  Volume2,
  VolumeX,
} from 'lucide-react';

// Level-up sound effect (optional)
const playLevelUpSound = () => {
  try {
    const audio = new Audio('/sounds/level-up.mp3');
    audio.volume = 0.3;
    audio.play().catch(() => {
      // Ignore if sound fails to play
    });
  } catch (error) {
    // Sound not available
  }
};

// Animated counting number component
const AnimatedNumber = ({ value, duration = 1000 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const increment = value / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value, duration]);

  return <span>{count}</span>;
};

const LevelUpCelebration = ({
  isOpen,
  onClose,
  newLevel,
  levelTitle,
  previousLevel,
  totalXP,
  onShare,
}) => {
  const [showContent, setShowContent] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const confettiTriggered = useRef(false);

  // Get milestone reward info
  const milestoneReward = LevelingService.getMilestoneReward(newLevel);
  const levelInfo = LevelingService.calculateLevel(totalXP);

  // Trigger full-screen confetti explosion
  const triggerConfetti = useCallback(() => {
    if (confettiTriggered.current) return;
    confettiTriggered.current = true;

    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = {
      startVelocity: 30,
      spread: 360,
      ticks: 60,
      zIndex: 9999,
      colors: ['#AEA36D', '#FFD700', '#FF6B6B', '#4ECDC4', '#A855F7', '#3B82F6']
    };

    const randomInRange = (min, max) => Math.random() * (max - min) + min;

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }

      const particleCount = 50 * (timeLeft / duration);

      // Fire from left
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });

      // Fire from right
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);
  }, []);

  // Animation sequence on open
  useEffect(() => {
    if (isOpen) {
      confettiTriggered.current = false;

      // Trigger confetti
      setTimeout(() => triggerConfetti(), 100);

      // Play sound
      if (soundEnabled) {
        setTimeout(() => playLevelUpSound(), 300);
      }

      // Stagger content appearance
      const timer = setTimeout(() => {
        setShowContent(true);
      }, 200);

      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
    }
  }, [isOpen, triggerConfetti, soundEnabled]);

  // Handle share to Discord
  const handleShareToDiscord = async () => {
    const shareText = `🎉 Level ${newLevel} - ${levelTitle}! ${totalXP?.toLocaleString()} Total XP`;

    // Try to call Discord webhook or just copy to clipboard
    try {
      await navigator.clipboard.writeText(shareText);
      onShare?.('discord', shareText);

      // Show toast notification
      if (typeof window !== 'undefined' && window.toast) {
        window.toast.success('Share text copied! Paste it in Discord 🎉');
      }
    } catch (err) {
      console.log('Share failed:', err);
    }
  };

  // Handle share
  const handleShare = async () => {
    const shareText = `🎉 I just reached Level ${newLevel} (${levelTitle}) on ODV Platform with ${totalXP?.toLocaleString()} XP! #ODVPlatform #LevelUp`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Level Up!',
          text: shareText,
        });
      } catch (err) {
        // User cancelled or error
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      await navigator.clipboard.writeText(shareText);
      onShare?.('copied');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="absolute inset-0 bg-black/90 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.75, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{
            type: 'spring',
            stiffness: 260,
            damping: 20
          }}
          className="relative z-10 max-w-lg w-full mx-4"
        >
        {/* Close Button */}
        <motion.button
          onClick={onClose}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="absolute -top-12 right-0 p-2 text-gray-400 hover:text-white transition-colors"
        >
          <X className="w-6 h-6" />
        </motion.button>

        {/* Sound Toggle */}
        <motion.button
          onClick={() => setSoundEnabled(!soundEnabled)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="absolute -top-12 right-12 p-2 text-gray-400 hover:text-white transition-colors"
        >
          {soundEnabled ? (
            <Volume2 className="w-6 h-6" />
          ) : (
            <VolumeX className="w-6 h-6" />
          )}
        </motion.button>

        {/* Card */}
        <div className="bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950 rounded-3xl overflow-hidden border border-[#aea36d]/30 shadow-2xl shadow-[#aea36d]/20">
          {/* Header Glow */}
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-48 bg-[#aea36d]/20 rounded-full blur-3xl"
          />

          {/* Top Section */}
          <div className="relative px-8 pt-10 pb-6 text-center">
            {/* Stars Animation */}
            {[
              { top: 4, left: 8, delay: 0, size: 6 },
              { top: 8, right: 12, delay: 0.2, size: 5 },
              { top: 16, left: 16, delay: 0.4, size: 4 },
              { top: 12, right: 20, delay: 0.6, size: 5 }
            ].map((star, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0, rotate: 0 }}
                animate={{
                  scale: [0, 1.2, 1],
                  rotate: [0, 180, 360],
                  opacity: [0, 1, 0.8]
                }}
                transition={{
                  delay: star.delay,
                  duration: 1,
                  repeat: Infinity,
                  repeatDelay: 2
                }}
                className="absolute"
                style={{
                  top: `${star.top * 4}px`,
                  ...(star.left ? { left: `${star.left * 4}px` } : { right: `${star.right * 4}px` })
                }}
              >
                {index % 2 === 0 ? (
                  <Star className={`w-${star.size} h-${star.size} text-yellow-400 fill-yellow-400`} />
                ) : (
                  <Sparkles className={`w-${star.size} h-${star.size} text-[#aea36d]`} />
                )}
              </motion.div>
            ))}

            {/* Level Badge */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                type: 'spring',
                stiffness: 260,
                damping: 15,
                delay: 0.3
              }}
              className="relative inline-block"
            >
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.3, 0.5, 0.3]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute inset-0 bg-[#aea36d]/30 rounded-full blur-xl"
              />
              <div className="relative p-6 rounded-full bg-gradient-to-br from-[#aea36d] via-yellow-500 to-[#aea36d] shadow-lg shadow-[#aea36d]/50">
                <motion.div
                  animate={{
                    rotate: [0, 10, -10, 0]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <Trophy className="w-16 h-16 text-gray-900" />
                </motion.div>
              </div>

              {/* Level Number Badge with counting animation */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 15,
                  delay: 0.6
                }}
                className="absolute -bottom-2 -right-2 px-4 py-1.5 rounded-full bg-gray-900 border-2 border-[#aea36d] shadow-lg"
              >
                <span className="text-2xl font-bold text-[#aea36d]">
                  <AnimatedNumber value={newLevel} duration={1000} />
                </span>
              </motion.div>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="mt-6 text-3xl font-bold text-white"
            >
              LEVEL UP!
            </motion.h1>

            {/* Previous Level Arrow */}
            {previousLevel && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.7, duration: 0.4 }}
                className="mt-2 flex items-center justify-center gap-2 text-gray-400"
              >
                <span>Level {previousLevel}</span>
                <ChevronRight className="w-4 h-4" />
                <span className="font-semibold text-[#aea36d]">Level {newLevel}</span>
              </motion.div>
            )}
          </div>

          {/* Level Title Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.5 }}
            className="px-8 py-6 bg-gradient-to-b from-transparent to-[#aea36d]/10"
          >
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#aea36d]/20 border border-[#aea36d]/30">
                <Crown className="w-5 h-5 text-[#aea36d]" />
                <span className="text-xl font-bold text-[#aea36d]">
                  {levelTitle || levelInfo.title}
                </span>
              </div>

              {/* XP Display with animation */}
              <div className="mt-4 flex items-center justify-center gap-2 text-lg">
                <Zap className="w-5 h-5 text-yellow-400" />
                <span className="font-semibold text-white">
                  <AnimatedNumber value={totalXP || 0} duration={1500} /> Total XP
                </span>
              </div>
            </div>
          </motion.div>

          {/* Rewards Section */}
          {milestoneReward && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1, duration: 0.5 }}
              className="px-8 py-4 border-t border-gray-800"
            >
              <div className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-purple-500/20 to-pink-500/10 border border-purple-500/30">
                <motion.div
                  animate={{
                    rotate: [0, 10, -10, 0],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="p-3 rounded-lg bg-purple-500/20"
                >
                  <Gift className="w-6 h-6 text-purple-400" />
                </motion.div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-purple-300">New Perks Unlocked</div>
                  <div className="text-white font-semibold">{milestoneReward.badge}</div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {milestoneReward.description}
                  </div>
                </div>
                {milestoneReward.bonus > 0 && (
                  <div className="text-right">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 15,
                        delay: 1.2
                      }}
                      className="text-lg font-bold text-green-400"
                    >
                      +{milestoneReward.bonus}
                    </motion.div>
                    <div className="text-xs text-gray-400">Bonus XP</div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.5 }}
            className="px-8 py-6 flex gap-3"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className="flex-1 py-3 px-4 rounded-xl bg-gray-800 text-white font-semibold hover:bg-gray-700 transition-colors"
            >
              Continue
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05, rotate: 2 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleShareToDiscord}
              className="flex items-center justify-center gap-2 py-3 px-6 rounded-xl bg-[#aea36d] text-gray-900 font-semibold hover:bg-[#c4b87d] transition-colors"
            >
              <Share2 className="w-5 h-5" />
              Share to Discord
            </motion.button>
          </motion.div>

          {/* Motivational Message */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.5 }}
            className="px-8 pb-6 text-center"
          >
            <p className="text-sm text-gray-400">
              {newLevel < 10 ? (
                <>Keep pushing! You're building your legacy. <Rocket className="inline w-4 h-4" /></>
              ) : newLevel < 50 ? (
                <>You're becoming a true professional! <Star className="inline w-4 h-4 text-yellow-400" /></>
              ) : newLevel < 100 ? (
                <>Elite status unlocked! You're in the top tier! <Crown className="inline w-4 h-4 text-[#aea36d]" /></>
              ) : (
                <>LEGENDARY! You've achieved greatness! <Trophy className="inline w-4 h-4 text-[#aea36d]" /></>
              )}
            </p>
          </motion.div>
        </div>
      </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default LevelUpCelebration;
